import type { Token, Tokens } from "marked";
import { MEASURE } from "./paper";

// Every picture a document points at, fetched once by address; one that cannot be read is left out for its alt text.
export async function fetchPictures(tokens: Token[], rasters: readonly string[]): Promise<Pictures> {
  const addresses = new Set<string>();
  const find = (list: Token[]) => {
    for (const token of list) {
      if (token.type === "image") addresses.add(token.href);
      if ("tokens" in token && Array.isArray(token.tokens)) find(token.tokens);
      if (token.type === "list") { for (const item of (token as Tokens.List).items) find(item.tokens); }
      if (token.type === "table") {
        const table = token as Tokens.Table;
        for (const cell of [...table.header, ...table.rows.flat()]) find(cell.tokens);
      }
    }
  };
  find(tokens);

  const found = await Promise.all(
    [...addresses].map(async (address) => [address, await fetchPicture(address, rasters)] as const),
  );

  return new Map(found.flatMap(([address, picture]) => picture ? [[address, picture] as const] : []));
}

// Drawn at its own size in points and never stretched to the measure, which is what a box fitted to the page does.
export function drawnSize(picture: Picture): { width: number; height: number } {
  const wide = picture.width * POINTS_PER_PIXEL;
  const tall = picture.height * POINTS_PER_PIXEL;
  const scale = Math.min(1, MEASURE / wide, PICTURE_HEIGHT / tall);
  const width = Math.max(1, Math.round(wide * scale));

  return { width, height: Math.max(1, Math.round(width * tall / wide)) };
}

// The width and height are what the file says in CSS pixels, before any writer has fitted it to a page.
export type Picture = ({ svg: string } | { raster: Blob }) & { width: number; height: number };

export type Pictures = Map<string, Picture>;

// A picture says how big it is in CSS pixels and a page is measured in points, which are three quarters of one.
export const POINTS_PER_PIXEL = 0.75;

async function fetchPicture(address: string, rasters: readonly string[]): Promise<Picture | undefined> {
  try {
    const response = await fetch(address, { signal: AbortSignal.timeout(PICTURE_WAIT) });
    if (!response.ok) return undefined;

    const type = response.headers.get("content-type") ?? "";
    // An SVG is kept as the drawing it is, a badge being the commonest picture in a README.
    if (type.includes("svg")) {
      const svg = await response.text();
      return { svg, ...svgSize(svg) };
    }

    const raster = rasters.find((accepted) => type.includes(accepted));
    if (!raster) return undefined;

    const blob = new Blob([await response.arrayBuffer()], { type: raster });
    return { raster: blob, ...await rasterSize(blob) };
  } catch {
    return undefined;
  }
}

// What the file says it is: the two attributes if it carries them, and the box it is drawn in if it does not.
function svgSize(svg: string): { width: number; height: number } {
  const root = new DOMParser().parseFromString(svg, "image/svg+xml").documentElement;
  const width = Number.parseFloat(root.getAttribute("width") ?? "");
  const height = Number.parseFloat(root.getAttribute("height") ?? "");
  if (width > 0 && height > 0) return { width, height };

  const box = (root.getAttribute("viewBox") ?? "").split(/[\s,]+/).map(Number);
  return box.length === 4 && box[2] > 0 && box[3] > 0
    ? { width: box[2], height: box[3] }
    : { width: PICTURE_FALLBACK, height: PICTURE_FALLBACK };
}

async function rasterSize(blob: Blob): Promise<{ width: number; height: number }> {
  let url = "";
  try {
    url = URL.createObjectURL(blob);
    const image = new Image();
    image.src = url;
    await image.decode();
    return { width: image.naturalWidth, height: image.naturalHeight };
  } catch {
    return { width: PICTURE_FALLBACK, height: PICTURE_FALLBACK };
  } finally {
    if (url) URL.revokeObjectURL(url);
  }
}

// The most of a page a picture is given, so one still leaves room for the words under it.
const PICTURE_HEIGHT = 520;

// A host that has not answered by then is one the document is written without.
const PICTURE_WAIT = 5000;

// What a picture with nothing to say about its own size is taken to be, neither vanishing nor taking the page.
const PICTURE_FALLBACK = 320;
